import { safeLocalStorage } from '@client/utils/storage/safe-storage';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/**
 * Simple key-value store for user settings
 * Can store ANY setting - tables, theme, notifications, etc.
 */
type UserSettingsStore = {
    settings: Record<string, any>;
    pendingChanges: string[];
    /**
     * Value snapshots waiting to be confirmed, keyed by the `{userId, orgId}`
     * scope that produced them. Keeping inactive scopes here prevents an org
     * transition from discarding the outgoing scope's debounced batch.
     */
    pendingSettingsByScope: Record<string, Record<string, any>>;
    isInitialized: boolean;
    /**
     * `${userId}:${orgId}` the cached settings belong to. The cache is
     * persisted under one browser-global key, so without this the next account
     * or organisation to use the browser would read (and re-upload) the
     * previous scope's settings instead of loading its own from the server.
     */
    scopeKey: string | null;
    /**
     * True only once the server has actually answered `getUserSettings` for the
     * active scope **in this app session**. Deliberately NOT persisted, so:
     * - a returning browser always issues one fresh read per scope/session
     *   instead of trusting its localStorage copy forever, and
     * - a failed load (which still flips `isInitialized` so the app can render)
     *   is never mistaken for "the server has no value for this key" by
     *   consumers that mirror local state back up.
     */
    hasServerSnapshot: boolean;
    /**
     * True once the active scope's initial server read has either succeeded or
     * failed in this app session. Unlike `isInitialized`, this is never
     * persisted, so it can release queued writes after a failed read without
     * letting a returning browser race its pending batch against a fresh GET.
     */
    hasSettledServerRead: boolean;

    setSetting: (key: string, value: any) => void;
    getSetting: (key: string, defaultValue?: any) => any;
    deleteSetting: (key: string) => void;
    initializeSettings: (settings: Record<string, any>, scopeKey?: string | null) => void;
    /** Activates `scopeKey`, retaining only its queued writes until a fresh server load. */
    resetForScope: (scopeKey: string | null) => void;
    addPendingChange: (key: string) => void;
    clearPendingChanges: () => void;
    /**
     * Clears the pending flag only for keys whose current value still matches
     * what was actually sent to the server. A key edited while the request was
     * in flight stays pending so the newer value is flushed too.
     */
    resolvePendingChanges: (syncedSettings: Record<string, any>, scopeKey?: string | null) => void;
    /** Marks the active scope's initial/retry GET as in flight. */
    markServerReadStarted: () => void;
    markAsInitialized: () => void;
};

/**
 * The identity a cached settings bag belongs to. Settings are stored per user
 * AND per organisation on the server, so both take part in the key.
 */
export function buildUserSettingsScopeKey(
    user: { id: string | number | bigint; orgId: string | number | bigint } | undefined | null,
): string | null {
    return user ? `${user.id}:${user.orgId}` : null;
}

/** Settings values are JSON, so structural equality is the meaningful comparison. */
function isSameSettingValue(a: unknown, b: unknown): boolean {
    if (a === b) {
        return true;
    }
    // `undefined` materialises a deletion tombstone, whereas `null` is a valid
    // stored JSON value. They must remain distinct while an upload is in flight.
    if (a === null || a === undefined || b === null || b === undefined) {
        return false;
    }
    return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Keys that would reach `Object.prototype` instead of creating an own property.
 * The store's untyped setters take arbitrary strings, so they are rejected at
 * every entry point rather than trusted to be developer-supplied constants.
 */
const UNSAFE_SETTING_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

function isUnsafeSettingKey(key: string): boolean {
    return UNSAFE_SETTING_KEYS.has(key);
}

/** Own-property assignment that cannot hit a prototype setter. */
function defineSetting(target: Record<string, any>, key: string, value: any): void {
    Object.defineProperty(target, key, { value, writable: true, enumerable: true, configurable: true });
}

function pendingSettingsForScope(
    pendingSettingsByScope: Record<string, Record<string, any>>,
    scopeKey: string | null,
): Record<string, any> | undefined {
    return scopeKey ? pendingSettingsByScope[scopeKey] : undefined;
}

/**
 * JSON-safe stand-in for "this key was deleted and the deletion has not been
 * flushed yet". `undefined` cannot be the tombstone: `pendingSettingsByScope`
 * is persisted through `createJSONStorage`, and `JSON.stringify` omits object
 * properties whose value is `undefined` — so an inactive scope's deletion would
 * vanish across a reload and the server value would never be retired.
 */
const DELETED_SETTING_MARKER = '__curiumDeletedSetting__';
const DELETED_SETTING = { [DELETED_SETTING_MARKER]: true } as const;

function isDeletedSettingMarker(value: unknown): boolean {
    return (
        typeof value === 'object' &&
        value !== null &&
        (value as Record<string, unknown>)[DELETED_SETTING_MARKER] === true
    );
}

/** The value a queued entry actually represents — a tombstone means "no value". */
export function materialisePendingValue(value: unknown): unknown {
    return isDeletedSettingMarker(value) ? undefined : value;
}

export const useUserSettingsStore = create<UserSettingsStore>()(
    persist(
        immer((set, get) => ({
            settings: {},
            pendingChanges: [],
            pendingSettingsByScope: {},
            isInitialized: false,
            scopeKey: null,
            hasServerSnapshot: false,
            hasSettledServerRead: false,

            setSetting: (key: string, value: any) => {
                set((state) => {
                    try {
                        if (isUnsafeSettingKey(key)) {
                            console.warn('Refusing to write unsafe user setting key:', key);
                            return;
                        }
                        // Plain assignment: `state.settings` is an immer draft
                        // (which rejects defineProperty), and the guard above
                        // has already ruled out the prototype-reaching keys.
                        state.settings[key] = value;
                        if (!state.pendingChanges.includes(key)) {
                            state.pendingChanges.push(key);
                        }
                        if (state.scopeKey) {
                            state.pendingSettingsByScope[state.scopeKey] ??= {};
                            state.pendingSettingsByScope[state.scopeKey][key] = value;
                        }
                    } catch (error) {
                        console.warn('Failed to set user setting:', key, error);
                    }
                });
            },

            getSetting: (key: string, defaultValue?: any) => {
                try {
                    const { settings } = get();
                    return settings[key] ?? defaultValue;
                } catch (error) {
                    console.warn('Failed to get user setting:', key, error);
                    return defaultValue;
                }
            },

            deleteSetting: (key: string) => {
                set((state) => {
                    try {
                        if (isUnsafeSettingKey(key)) {
                            console.warn('Refusing to delete unsafe user setting key:', key);
                            return;
                        }
                        delete state.settings[key];
                        if (!state.pendingChanges.includes(key)) {
                            state.pendingChanges.push(key);
                        }
                        if (state.scopeKey) {
                            state.pendingSettingsByScope[state.scopeKey] ??= {};
                            state.pendingSettingsByScope[state.scopeKey][key] = DELETED_SETTING;
                        }
                    } catch (error) {
                        console.warn('Failed to delete user setting:', key, error);
                    }
                });
            },

            initializeSettings: (newSettings: Record<string, any>, scopeKey?: string | null) => {
                set((state) => {
                    try {
                        // Unflushed local writes are newer than this snapshot —
                        // a background reload of an already-cached bag must not
                        // clobber a change the user just made, so they survive
                        // (and stay pending until the sync confirms them).
                        const merged: Record<string, any> = {};
                        for (const [key, value] of Object.entries(newSettings)) {
                            if (!isUnsafeSettingKey(key)) {
                                defineSetting(merged, key, value);
                            }
                        }
                        for (const key of state.pendingChanges) {
                            if (isUnsafeSettingKey(key)) {
                                continue;
                            }
                            if (Object.hasOwn(state.settings, key)) {
                                defineSetting(merged, key, state.settings[key]);
                            } else {
                                delete merged[key];
                            }
                        }
                        state.settings = merged;
                        state.isInitialized = true;
                        state.hasServerSnapshot = true;
                        state.hasSettledServerRead = true;
                        if (scopeKey !== undefined) {
                            state.scopeKey = scopeKey;
                        }
                    } catch (error) {
                        console.warn('Failed to initialize settings:', error);
                        state.isInitialized = true;
                        state.hasSettledServerRead = true;
                    }
                });
            },

            resetForScope: (scopeKey: string | null) => {
                set((state) => {
                    const pendingSettings = pendingSettingsForScope(state.pendingSettingsByScope, scopeKey);
                    // A tombstoned key stays *pending* (its deletion still has
                    // to reach the server) but must not come back as a value.
                    const restoredSettings: Record<string, any> = {};
                    const restoredPendingChanges: string[] = [];
                    for (const [key, value] of Object.entries(pendingSettings ?? {})) {
                        restoredPendingChanges.push(key);
                        if (!isDeletedSettingMarker(value)) {
                            defineSetting(restoredSettings, key, value);
                        }
                    }
                    state.settings = restoredSettings;
                    state.pendingChanges = restoredPendingChanges;
                    state.isInitialized = false;
                    state.hasServerSnapshot = false;
                    state.hasSettledServerRead = false;
                    state.scopeKey = scopeKey;
                });
            },

            addPendingChange: (key: string) => {
                set((state) => {
                    try {
                        if (isUnsafeSettingKey(key)) {
                            console.warn('Refusing to queue unsafe user setting key:', key);
                            return;
                        }
                        if (!state.pendingChanges.includes(key)) {
                            state.pendingChanges.push(key);
                        }
                        if (state.scopeKey) {
                            state.pendingSettingsByScope[state.scopeKey] ??= {};
                            state.pendingSettingsByScope[state.scopeKey][key] = Object.hasOwn(state.settings, key)
                                ? state.settings[key]
                                : DELETED_SETTING;
                        }
                    } catch (error) {
                        console.warn('Failed to add pending change:', key, error);
                    }
                });
            },

            clearPendingChanges: () => {
                set((state) => {
                    try {
                        state.pendingChanges = [];
                        if (state.scopeKey) {
                            delete state.pendingSettingsByScope[state.scopeKey];
                        }
                    } catch (error) {
                        // Silently fail
                        console.warn('Failed to clear pending changes:', error);
                    }
                });
            },

            resolvePendingChanges: (syncedSettings: Record<string, any>, syncedScopeKey?: string | null) => {
                set((state) => {
                    const scopeKey = syncedScopeKey === undefined ? state.scopeKey : syncedScopeKey;
                    const scopedPending = pendingSettingsForScope(state.pendingSettingsByScope, scopeKey);
                    if (scopedPending) {
                        for (const [key, value] of Object.entries(syncedSettings)) {
                            if (isSameSettingValue(materialisePendingValue(scopedPending[key]), value)) {
                                delete scopedPending[key];
                            }
                        }
                        if (scopeKey && Object.keys(scopedPending).length === 0) {
                            delete state.pendingSettingsByScope[scopeKey];
                        }
                    }
                    if (state.scopeKey !== scopeKey) {
                        return;
                    }
                    state.pendingChanges = state.pendingChanges.filter((key) => {
                        if (!(key in syncedSettings)) {
                            return true;
                        }
                        return !isSameSettingValue(state.settings[key], syncedSettings[key]);
                    });
                });
            },

            markServerReadStarted: () => {
                set((state) => {
                    state.hasSettledServerRead = false;
                });
            },

            // NOTE: deliberately does not set `hasServerSnapshot` — this is the
            // "the load failed, let the app render anyway" path, and an empty
            // bag from a failed load must never be read as the server's answer.
            markAsInitialized: () => {
                set((state) => {
                    state.isInitialized = true;
                    state.hasSettledServerRead = true;
                });
            },
        })),
        {
            name: 'user-settings-store',
            storage: createJSONStorage(() => safeLocalStorage),
            partialize: (state) => ({
                settings: state.settings,
                isInitialized: state.isInitialized,
                scopeKey: state.scopeKey,
                // Persisted so a tab closed inside the 500 ms sync debounce does
                // not silently drop the write. The scoped snapshots survive org
                // transitions, the next visit/boot re-arms the active scope's
                // flush, and `initializeSettings` keeps its local values over
                // the server snapshot until they are confirmed.
                pendingChanges: state.pendingChanges,
                // Unconfirmed writes are user data: retain every scope until a
                // successful upload removes its batch. A fixed scope-count cap
                // silently lost older offline edits after a reload.
                pendingSettingsByScope: state.pendingSettingsByScope,
            }),
        },
    ),
);
